import React, { FunctionComponent } from 'react'
import styled, { css } from 'styled-components'
import {
  CircleLightGrayIcon as LightGrayCircle,
  CircleDarkGrayIcon as DarkGrayCircle,
  SaturatedBlueDotIcon,
  Circle242Icon,
  HeaderDashedCircleIcon,
  DiamondIcon,
} from '../../assets/icons'
import { getScreenBreakpointSize } from '../../utils/getScreenBreakpointSize'
import { ScreenBreakpointSize } from '../../utils/enums'
import { LinkButton } from '../common/button'
import Sale from '../../typings/Sale'
import { Countdown } from './Countdown'

interface IProps {
  sale: typeof Sale
  showCta?: boolean
  showCountdown?: boolean
  size?: 'small' | 'large'
}

interface BannerProps {
  size: 'small' | 'large'
}

const spanWholeContainer = css`
  position: absolute;
  width: 100%;
  height: 100%;
`

const centered = css`
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const decorativeAndAbsolute = css`
  position: absolute;
  user-select: none;
`

const StyledBanner = styled.section<BannerProps>`
  background: #181818;
  min-height: ${(props) => (props.size === 'large' ? '19rem' : '13rem')};
  margin-top: -15px;
  margin-bottom: ${(props) => (props.size === 'large' ? '5rem' : '1rem')};
  position: relative;
  overflow: hidden;
  z-index: 0;
  font-family: var(--lato);
  border-radius: ${(props) => (props.size === 'large' ? '0rem' : '0.3rem')};

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.ExtraLarge)}) {
    margin-top: -48px;
  }
`

const Circle = styled.img`
  ${decorativeAndAbsolute}
  ${centered}

  &[data-name="c-1"] {
    width: 100%;
  }

  &[data-name='c-2'] {
    width: 92.86%;
  }

  &[data-name='c-3'] {
    width: 85.72%;
  }

  &[data-name='c-4'] {
    width: 78.58%;
  }

  &[data-name='c-5'] {
    width: 71.44%;
  }

  &[data-name='c-6'] {
    width: 64.3%;
  }

  &[data-name='c-7'] {
    width: 57.16%;
  }

  &[data-name='c-8'] {
    width: 50.02%;
  }

  &[data-name='c-9'] {
    width: 42.88%;
  }

  &[data-name='c-10'] {
    width: 35.74%;
  }

  &[data-name='c-11'] {
    width: 28.6%;
  }

  &[data-name='c-12'] {
    width: 21.46%;
  }

  &[data-name='c-13'] {
    width: 14.32%;
  }

  &[data-name='c-14'] {
    width: 7.18%;
  }
`

const Decorative = styled.img`
  ${decorativeAndAbsolute}

  &[data-name="de-1"] {
    width: 4.5rem;
    height: 4.5rem;
    bottom: 6%;
    left: 3.75%;
    filter: invert(1);
  }

  &[data-name='de-2'] {
    width: 3rem;
    height: 3rem;
    top: 15%;
    left: 9%;
    filter: invert(1);
    opacity: 0.7;
  }

  &[data-name='de-3'] {
    width: 1.85rem;
    height: 1.85rem;
    bottom: 15%;
    left: 31.25%;
    opacity: 0.5;
  }

  &[data-name='de-4'],
  &[data-name='de-5'],
  &[data-name='de-6'] {
    width: 1.35rem;
    height: 1.35rem;
    filter: invert(1);
  }

  &[data-name='de-4'] {
    bottom: 45%;
    left: 26%;
  }

  &[data-name='de-5'] {
    top: 8%;
    left: 37.5%;
  }

  &[data-name='de-6'] {
    top: 9%;
    right: 6%;
  }

  &[data-name='de-7'] {
    width: 1.65rem;
    height: 1.65rem;
    bottom: 20%;
    right: 12.5%;
    filter: invert(1);
  }

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Small, -1)}) {
    display: none;
  }
`

const BannerCopy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  ${spanWholeContainer}
  color: #fff;
  z-index: 1;
  padding: 0 2rem;
`

const BackgroundDecorations = styled.div`
  ${spanWholeContainer}
  ${centered}
  z-index: 0;
`

const CircleBackground = styled.div`
  ${spanWholeContainer}
  z-index: -1;
  user-select: none;
  opacity: 0.4;

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Small, -1)}) {
    width: 150%;
    ${centered}
  }
`

const StyledHeading = styled.div`
  font-weight: 900;
  font-size: 1.75rem;

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Small, -1)}) {
    font-size: 1.5rem;
  }
`

const StyledSubheading = styled.div`
  color: var(--athens-gray);
  margin: 0.5rem 0 0.5rem;

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Small, -1)}) {
    margin-top: 1rem;
  }
`

const BlueDotImg: FunctionComponent = (props) => <img aria-hidden={true} src={SaturatedBlueDotIcon} {...props} />

const StyledBlueDot = styled(BlueDotImg)`
  ${decorativeAndAbsolute}

  &[data-name='bd-1'] {
    top: 5%;
    left: 40%;
  }

  &[data-name='bd-2'] {
    bottom: 24%;
    left: 11%;
  }

  &[data-name='bd-3'] {
    bottom: 28%;
    right: 10.75%;
  }

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Small, -1)}) {
    &[data-name='bd-1'] {
      top: 10%;
      left: 90%;
    }

    &[data-name='bd-2'] {
      bottom: 10%;
      left: 10%;
    }

    &[data-name='bd-2'] {
      display: none;
    }
  }
`

const DiamondImg: FunctionComponent = (props) => <img aria-hidden={true} src={DiamondIcon} {...props} />

const StyledDiamondIcon = styled(DiamondImg)`
  ${decorativeAndAbsolute}

  filter: invert(1);

  &[data-name='di-1'] {
    width: 4rem;
    height: 4rem;
    bottom: 30%;
    right: 25%;
  }

  &[data-name='di-2'] {
    width: 3.5rem;
    height: 3rem;
    bottom: -1rem;
    right: 38%;
  }

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Small, -1)}) {
    &[data-name='di-1'] {
      width: 2rem;
      height: 2rem;
      top: 2%;
      left: 5%;
    }

    &[data-name='di-2'] {
      right: auto;
      left: -1rem;
    }
  }
`

const DashedCircle2 = styled.img`
  ${decorativeAndAbsolute}
  width: 1.35rem;
  height: 1.35rem;
  top: 8%;
  left: 61%;
  filter: invert(1);

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Small, -1)}) {
    width: 1.65rem;
    height: 1.65rem;
    top: 85%;
    left: 85%;
  }
`

const SaleBanner: FunctionComponent<IProps> = ({ sale, showCta = true, showCountdown = false, size = 'large' }) => {
  return (
    <StyledBanner size={size} aria-label={'Sale banner'}>
      <BannerCopy>
        <StyledHeading>{sale.title}</StyledHeading>
        <StyledSubheading>{sale.subtitle}</StyledSubheading>
        {showCountdown && <Countdown />}
        {showCta && (
          <div className="mt-5">
            <LinkButton text={'Get the deal'} href={'/plans'} />
          </div>
        )}
      </BannerCopy>
      <BackgroundDecorations>
        <Decorative aria-hidden={true} src={Circle242Icon} data-name={'de-1'} />
        <Decorative aria-hidden={true} src={DiamondIcon} data-name={'de-2'} />
        <Decorative aria-hidden={true} src={LightGrayCircle} data-name={'de-3'} />
        <Decorative aria-hidden={true} src={HeaderDashedCircleIcon} data-name={'de-4'} />
        <DashedCircle2 aria-hidden={true} src={HeaderDashedCircleIcon} />
        <StyledDiamondIcon data-name={'di-1'} />
        <StyledDiamondIcon data-name={'di-2'} />
        <Decorative aria-hidden={true} src={HeaderDashedCircleIcon} data-name={'de-5'} />
        <Decorative aria-hidden={true} src={HeaderDashedCircleIcon} data-name={'de-6'} />
        <StyledBlueDot data-name={'bd-1'} />
        <StyledBlueDot data-name={'bd-2'} />
        <StyledBlueDot data-name={'bd-3'} />
        <Decorative aria-hidden={true} src={Circle242Icon} data-name={'de-7'} />
      </BackgroundDecorations>
      <CircleBackground>
        <Circle aria-hidden={true} src={LightGrayCircle} data-name={'c-1'} />
        <Circle aria-hidden={true} src={DarkGrayCircle} data-name={'c-2'} />
        <Circle aria-hidden={true} src={LightGrayCircle} data-name={'c-3'} />
        <Circle aria-hidden={true} src={DarkGrayCircle} data-name={'c-4'} />
        <Circle aria-hidden={true} src={LightGrayCircle} data-name={'c-5'} />
        <Circle aria-hidden={true} src={DarkGrayCircle} data-name={'c-6'} />
        <Circle aria-hidden={true} src={LightGrayCircle} data-name={'c-7'} />
        <Circle aria-hidden={true} src={DarkGrayCircle} data-name={'c-8'} />
        <Circle aria-hidden={true} src={LightGrayCircle} data-name={'c-9'} />
        <Circle aria-hidden={true} src={DarkGrayCircle} data-name={'c-10'} />
        <Circle aria-hidden={true} src={LightGrayCircle} data-name={'c-11'} />
        <Circle aria-hidden={true} src={DarkGrayCircle} data-name={'c-12'} />
        <Circle aria-hidden={true} src={LightGrayCircle} data-name={'c-13'} />
        <Circle aria-hidden={true} src={DarkGrayCircle} data-name={'c-14'} />
      </CircleBackground>
    </StyledBanner>
  )
}

export default SaleBanner
